/**
 * Format time as HH:mm string
 */
export const formatTime = (hour: number, minute: number = 0): string => {
	return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
};

/**
 * Get day of week label in Japanese
 */
export const getDayOfWeekLabel = (date: Date): string => {
	const days = ["日", "月", "火", "水", "木", "金", "土"];
	return days[date.getDay()];
};

/**
 * Get array of 7 dates for the week containing the given start date
 * Dates are normalized to midnight in local timezone for consistent comparison
 */
export const getWeekDates = (startDate: Date): Date[] => {
	const dates: Date[] = [];
	const start = new Date(startDate);
	start.setHours(0, 0, 0, 0);
	start.setDate(start.getDate() - start.getDay());

	for (let i = 0; i < 7; i++) {
		const date = new Date(start);
		date.setDate(start.getDate() + i);
		date.setHours(0, 0, 0, 0);
		dates.push(date);
	}
	return dates;
};

/**
 * Check if two dates are the same day (in local timezone)
 */
export const isSameDay = (
	date1: Date | string,
	date2: Date | string,
): boolean => {
	const d1 = typeof date1 === "string" ? new Date(date1) : date1;
	const d2 = typeof date2 === "string" ? new Date(date2) : date2;
	return (
		d1.getFullYear() === d2.getFullYear() &&
		d1.getMonth() === d2.getMonth() &&
		d1.getDate() === d2.getDate()
	);
};

/**
 * Check if a date is today
 */
export const isToday = (date: Date): boolean => {
	const today = new Date();
	return isSameDay(date, today);
};

/**
 * Check if a date is in the past (before today)
 */
export const isPastDay = (date: Date): boolean => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const compareDate = new Date(date);
	compareDate.setHours(0, 0, 0, 0);
	return compareDate < today;
};

/**
 * Check if a date is Saturday
 */
export const isSaturday = (date: Date): boolean => date.getDay() === 6;

/**
 * Check if a date is Sunday
 */
export const isSunday = (date: Date): boolean => date.getDay() === 0;

/**
 * Get month/year string for display (handles week spanning two months)
 */
export const getMonthYearDisplay = (weekDates: Date[]): string => {
	const firstDate = weekDates[0];
	const lastDate = weekDates[6];
	if (firstDate.getMonth() === lastDate.getMonth()) {
		return `${firstDate.getFullYear()}年${firstDate.getMonth() + 1}月`;
	}
	return `${firstDate.getFullYear()}年${firstDate.getMonth() + 1}月〜${lastDate.getMonth() + 1}月`;
};

/**
 * Get week range string for display
 */
export const getWeekRangeDisplay = (weekDates: Date[]): string => {
	const firstDate = weekDates[0];
	const lastDate = weekDates[6];
	const formatDate = (d: Date) => {
		return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
	};
	return `${formatDate(firstDate)}〜${formatDate(lastDate)}`;
};

/**
 * Calculate new week start date based on navigation direction
 */
export const calculateWeekNavigation = (
	currentWeekStart: Date,
	direction: "prev" | "next" | "prevMonth" | "nextMonth",
): Date => {
	const newDate = new Date(currentWeekStart);
	switch (direction) {
		case "prev":
			newDate.setDate(newDate.getDate() - 7);
			break;
		case "next":
			newDate.setDate(newDate.getDate() + 7);
			break;
		case "prevMonth":
			newDate.setDate(newDate.getDate() - 14);
			break;
		case "nextMonth":
			newDate.setDate(newDate.getDate() + 14);
			break;
	}
	return newDate;
};

/**
 * Get start of current week (Sunday) at midnight
 */
export const getStartOfCurrentWeek = (): Date => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	today.setDate(today.getDate() - today.getDay());
	return today;
};

/**
 * Generate time slots based on start/end hours and duration
 */
export const generateTimeSlots = (
	startHour: number,
	endHour: number,
	slotDuration: number,
): string[] => {
	const slots: string[] = [];
	const startMinutes = startHour * 60;
	const endMinutes = endHour * 60;

	for (
		let minutes = startMinutes;
		minutes + slotDuration <= endMinutes;
		minutes += slotDuration
	) {
		const hour = Math.floor(minutes / 60);
		const minute = minutes % 60;
		slots.push(formatTime(hour, minute));
	}
	return slots;
};

/**
 * Check if a specific time slot is in the past (JST)
 */
export const isPastSlot = (date: Date, slotTime: string): boolean => {
	const now = new Date();
	const [hour, minute] = slotTime.split(":").map(Number);
	const year = date.getFullYear();
	const month = date.getMonth();
	const day = date.getDate();
	const slotUtc = new Date(Date.UTC(year, month, day, hour - 9, minute, 0, 0));
	return slotUtc < now;
};

/**
 * Get day header color based on day of week
 */
export const getDayHeaderColor = (date: Date): string => {
	if (isSunday(date)) return "#dc2626";
	if (isSaturday(date)) return "#2563eb";
	return "#374151";
};

/**
 * Calculate time range from occurrences (JST)
 * Returns the minimum and maximum hours needed to display all occurrences
 */
export const calculateTimeRangeFromOccurrences = (
	occurrences: Array<{ startAt: string | Date; endAt: string | Date }>,
	defaultStartHour = 0,
	defaultEndHour = 23,
): { startHour: number; endHour: number } => {
	if (!occurrences || occurrences.length === 0) {
		return { startHour: defaultStartHour, endHour: defaultEndHour };
	}

	const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
	let minHour = 23;
	let maxHour = 0;

	occurrences.forEach((occ) => {
		if (!occ.startAt || !occ.endAt) return;

		const occStart = new Date(
			typeof occ.startAt === "string" ? occ.startAt : occ.startAt,
		);
		const occEnd = new Date(
			typeof occ.endAt === "string" ? occ.endAt : occ.endAt,
		);

		if (Number.isNaN(occStart.getTime()) || Number.isNaN(occEnd.getTime())) {
			return;
		}

		const jstStart = new Date(occStart.getTime() + JST_OFFSET_MS);
		const jstEnd = new Date(occEnd.getTime() + JST_OFFSET_MS);

		const occStartHour = jstStart.getUTCHours();
		const occStartMinute = jstStart.getUTCMinutes();
		const occStartTotalMinutes = occStartHour * 60 + occStartMinute;

		const occEndHour = jstEnd.getUTCHours();
		const occEndMinute = jstEnd.getUTCMinutes();
		const occEndTotalMinutes = occEndHour * 60 + occEndMinute;

		const occMinHour = Math.floor(occStartTotalMinutes / 60);
		const occMaxHour = Math.ceil(occEndTotalMinutes / 60);

		if (occMinHour < minHour) {
			minHour = Math.max(0, occMinHour);
		}
		if (occMaxHour > maxHour) {
			maxHour = Math.min(23, occMaxHour);
		}
	});

	if (minHour > maxHour) {
		return { startHour: defaultStartHour, endHour: defaultEndHour };
	}

	return {
		startHour: Math.max(0, minHour),
		endHour: Math.min(23, maxHour),
	};
};
